'use client';

import {useEffect, useMemo} from 'react';
import {createPortal} from 'react-dom';
import {Button} from '../atoms/Button';
import {PanelHeaderRow} from '../atoms/PanelHeaderRow';
import {Spinner} from '../atoms/Spinner';
import {Typography} from '../atoms/Typography';
import {useMatchQuery} from '../hooks/useMatchQuery';
import {BottomSheet} from '../molecules/BottomSheet';
import {SlideoutPanel} from '../molecules/SlideoutPanel';
import {Modal} from './Modal';

// Intended for more complex modal data, this opens the right-side
// slideout-panel (used for login/signup) on large screens,
// and a full-page slide up modal in small screens.

// REQUIRES isOpen, onClose, as well as the following:
// also REQUIRES either children:
//    (will render children with X close button in upper right in an otherwise empty modal)
// OR: ALL the below:
//  title: (string),
//  body: (string),
//  primaryAction: (func called on primary CTA),

// Additional optional props:
//  primaryText: (string:Submit - text for primary button,
//  secondaryText: (string:Cancel - text for secondary button)
//  primaryAction: (func:primaryAction - action for primary button),
//  secondaryAction: (func:onClose - action for secondary button)
//  showCover: (bool:False - If true, shows semi-transparent dark overlay when panel is open),
//  modalId: (string:overlay - identifier for this modal, useful for custom qa-id),
//  closeQaId: (string:overlay-header_back_button - for qa-ids on close button/X icon)

export const MOBILE_VIEWS = Object.freeze({
  MODAL: 'modal',
  BOTTOM_SHEET: 'bottom-sheet',
});

export const ResponsivePanel = ({
  body = '',
  portalMode = true,
  showHeader = true,
  onClose,
  primaryAction = onClose,
  secondaryAction = onClose,
  primaryText = 'Submit',
  secondaryText = 'Cancel',
  showSpinner = false,
  actionText = 'One moment please...',
  isOpen,
  showCover = false,
  showTitle = true,
  titleVariant = 'header4',
  modalId = 'overlay',
  showBottomNav = true,
  className = null,
  renderMobileViewAs = MOBILE_VIEWS.MODAL,
  hideHeaderDivider = false,
  childProvidesSideMargin = false,
  trapFocus = true,
  focusTrapPaused,
  showBackBtn = false,
  onBackBtnClick,
  fallbackFocus,
  style,
  disableOverlayClick,
  bottomSheetHeightSetting = 'tall',
  ...props
}) => {
  const {matches: mdWindow} = useMatchQuery(`(min-width: 48rem)`);

  const title = useMemo(() => {
    if (showSpinner) return actionText;
    if (!showTitle) return null;
    return props.title || `${primaryText}?` || 'Are you sure?';
  }, [showSpinner, actionText, showTitle, props.title, primaryText]);

  const hideDivider = hideHeaderDivider || !title;

  let children = showSpinner ? <Spinner center /> : props.children;

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isOpen]);

  const getModalChildren = () => (
    <div className={className}>
      <div className="responsive-modal-body">
        <Typography variant="paragraph2">{body}</Typography>
      </div>
      {showBottomNav && (
        <div className="responsive-modal-button-row">
          <Button
            variant="secondary"
            size="large"
            onClick={secondaryAction}
            data-qa-id={`${modalId}-cancel`}
          >
            {secondaryText}
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={primaryAction}
            data-qa-id={`${modalId}-submit`}
          >
            {primaryText}
          </Button>
        </div>
      )}
    </div>
  );

  if (!children)
    if (mdWindow) {
      children = getModalChildren();
    } else {
      children = (
        <>
          {showHeader && (
            <PanelHeaderRow
              title={title}
              showTitle={showTitle}
              onClose={onClose}
              qaId={`${modalId}-header`}
              backArrow={showBackBtn}
              titleVariant={titleVariant}
            />
          )}
          {getModalChildren()}
        </>
      );
    }

  if (!mdWindow) {
    if (renderMobileViewAs === MOBILE_VIEWS.BOTTOM_SHEET) {
      return (
        <BottomSheet
          onClose={onClose}
          content={children}
          title={title}
          titleVariant={titleVariant}
          className={className}
          data-qa-id={modalId}
          in={isOpen}
          heightSetting={bottomSheetHeightSetting}
          showTitle={showTitle}
          hideHeaderDivider={hideDivider}
          childProvidesSideMargin={childProvidesSideMargin}
          showBackBtn={showBackBtn}
          onBackBtnClick={onBackBtnClick}
        />
      );
    }
    if (children) {
      children = (
        <>
          {showHeader && (
            <PanelHeaderRow
              title={title}
              showTitle={showTitle}
              onClose={onClose}
              qaId={`${modalId}-header`}
              backArrow={showBackBtn}
              titleVariant={titleVariant}
            />
          )}
          {children}
        </>
      );
    }
    return (
      <Modal
        className={className}
        isOpen={isOpen}
        onClose={onClose}
        onClick={onClose}
        modalId={`responsive-modal ${modalId || ''}`}
        style={{content: style}}
      >
        {children}
      </Modal>
    );
  }

  const Component = (
    <SlideoutPanel
      isOpen={isOpen}
      onClose={onClose}
      closeQaId={`${modalId}-header`}
      modalId={`responsive-modal ${modalId || ''}`}
      title={showSpinner ? actionText : title || null}
      showSpinner={showSpinner}
      showHeader={showHeader}
      showTitle={showTitle}
      titleVariant={titleVariant}
      qaId="responsive-modal-panel"
      backArrow={showBackBtn}
      showCover={showCover}
      trapFocus={trapFocus}
      focusTrapPaused={focusTrapPaused}
      fallbackFocus={fallbackFocus}
      className={className}
      style={style}
      disableOverlayClick={disableOverlayClick}
    >
      {children}
    </SlideoutPanel>
  );

  if (portalMode) {
    return createPortal(Component, document.getElementById('pbModalRoot'));
  }

  return Component;
};
